import { useState } from 'react'
import { Bookmark, FileDown, Link, Shield } from 'lucide-react'
import type { WireArticleFull } from '../../../api/types'
import { useArticlePdf } from '../../../pdf/useArticlePdf'
import { useToggleSavedArticle } from '../../../api/hooks'

interface ArticleToolbarProps {
  article: WireArticleFull
  adminMode?: boolean
  canToggleAdminMode?: boolean
  onToggleAdminMode?: () => void
}

export function ArticleToolbar({ article, adminMode = false, canToggleAdminMode = false, onToggleAdminMode }: ArticleToolbarProps) {
  const { downloadPdf, isGenerating } = useArticlePdf()
  const [copied, setCopied] = useState(false)

  const [saved, setSaved] = useState(article.saved ?? false)
  const toggleSaved = useToggleSavedArticle()

  function handleToggleSave() {
    const nextSaved = !saved
    setSaved(nextSaved)
    toggleSaved.mutate(
      { article, nextSaved },
      {
        onSuccess: (result) => setSaved(result.saved),
        onError: () => setSaved(!nextSaved),
      },
    )
  }

  function handleShare() {
    const url = `${window.location.origin}/wire/articles/${article.id}?nav_method=gen_link`
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div className="flex items-center justify-end gap-4 border-t border-line-subtle pt-3">
      {canToggleAdminMode && onToggleAdminMode && (
        <button
          onClick={onToggleAdminMode}
          aria-pressed={adminMode}
          className="flex items-center gap-1.5 text-xs font-medium text-fg-secondary transition-colors hover:text-fg"
        >
          <Shield size={14} fill={adminMode ? 'currentColor' : 'none'} />
          {adminMode ? 'Reader Mode' : 'Admin Mode'}
        </button>
      )}
      <button
        onClick={handleToggleSave}
        disabled={toggleSaved.isPending}
        aria-pressed={saved}
        className="flex items-center gap-1.5 text-xs font-medium text-fg-secondary transition-colors hover:text-fg disabled:opacity-50"
      >
        <Bookmark size={14} fill={saved ? 'currentColor' : 'none'} />
        {saved ? 'Saved' : 'Save'}
      </button>
      <button
        onClick={handleShare}
        className="flex items-center gap-1.5 text-xs font-medium text-fg-secondary transition-colors hover:text-fg"
      >
        <Link size={14} />
        {copied ? 'Copied!' : 'Share'}
      </button>
      <button
        onClick={() => downloadPdf(article)}
        disabled={isGenerating}
        className="flex items-center gap-1.5 text-xs font-medium text-fg-secondary transition-colors hover:text-fg disabled:opacity-50"
      >
        <FileDown size={14} />
        {isGenerating ? 'Building…' : 'Download as PDF'}
      </button>
    </div>
  )
}
